import React, { useState } from 'react';
import { SimpleModalTW } from '../../../../tailwindComponents/modals/SimpleModalTW';
import { HeaderText2TW } from '../../../../tailwindComponents/HeaderTextTW/HeaderTextTW';
import { ISignerResponse } from '../../../../types/signingAppointment/ISignerResponse';
import { Form, Formik } from 'formik';
import { ButtonTW, ColorlessButtonTW } from '../../../../tailwindComponents/buttonTW/ButtonTW';
import { EditSignerFormData } from './EditSignerFormData';
import { TextFieldTW } from '../../../../tailwindComponents/textInputTW/TextFieldTW';
import { PhoneIcon } from '@heroicons/react/24/outline';
import { Header5Text, SpinnerSize } from '../../../../ui-toolkit';
import { SpinnerTW } from '../../../../tailwindComponents/spinnerTW/SpinnerTW';
import { TrashIcon } from '@heroicons/react/20/solid';
import * as yup from 'yup';
import { ISigningAppointmentResponse } from '../../../../types/signingAppointment/ISigningAppointmentResponse';
import { IEditSignerRequest } from '../../../../types/signingAppointment/IEditSignerRequest';
import { Show } from '../../../shared/Show';
import { SignerDeletionConfirmation } from './SignerDeletionConfirmation';
import { PhoneNumberFieldTW } from 'tailwindComponents/phoneNumberFieldTW/PhoneNumberFieldTW';
import { ISigningAppointmentEditFormData } from '../../../../types/signingAppointment/ISigningAppointmentEditFormData';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../types/shared/UUID';
import { useSelectAppointmentFromPathParam } from '../../../../utilities/order/scheduling/useSomeAppointmentFromParam';

export interface SignerEditionModalProps {
  signerToEdit?: ISignerResponse;
  onClose: () => void;
  onSaveEditions: (formData: ISigningAppointmentEditFormData) => Promise<void>;
}

export const SignerEditionModal = ({ signerToEdit, onClose, onSaveEditions }: SignerEditionModalProps) => {
  const { appointmentFromPath } = useSelectAppointmentFromPathParam();

  const open = !!signerToEdit;
  if (!appointmentFromPath) return null;
  return (
    <SimpleModalTW open={open} setOpen={onClose}>
      {signerToEdit && <EditSignerForm onClose={onClose} signer={signerToEdit} onSaveEditions={onSaveEditions} />}
    </SimpleModalTW>
  );
};

interface EditSignerFormProps {
  signer: ISignerResponse;
  onClose: () => void;
  onSaveEditions: (formData: ISigningAppointmentEditFormData) => Promise<void>;
}

const EditSignerForm = ({ signer, onClose, onSaveEditions }: EditSignerFormProps) => {
  const { appointmentFromPath } = useSelectAppointmentFromPathParam();
  const { orderId } = useParams<{ orderId: UUID }>();
  const [showDeletionConfirmation, setShowDeletionConfirmation] = useState<boolean>(false);

  const onCancelConfirmation = () => {
    setShowDeletionConfirmation(false);
  };

  const onDeleteClick = () => {
    setShowDeletionConfirmation(true);
  };
  if (!appointmentFromPath) return null;

  if (showDeletionConfirmation) {
    return (
      <SignerDeletionConfirmation
        signerId={signer.id}
        onClose={onClose}
        onCancel={onCancelConfirmation}
        orderId={orderId}
        appointmentId={appointmentFromPath.id}
      />
    );
  }

  return (
    <div>
      <section>
        <HeaderText2TW>Edit a Signer</HeaderText2TW>
      </section>

      <section className={'mt-4'}>
        <Formik
          initialValues={buildInitialFormData(signer)}
          validationSchema={EditSignerValidationSchema}
          onSubmit={async (values: EditSignerFormData) => {
            const request = buildUpdateRequest(values, appointmentFromPath);
            await onSaveEditions(request);
            onClose();
          }}
        >
          {({ isValid, isSubmitting }) => (
            <>
              <Form>
                <section>
                  <div className={'mb-2 flex gap-2'}>
                    <TextFieldTW
                      isRequired={true}
                      data-test-id={'edit-signer-first-name'}
                      name={'firstName'}
                      labelText={'First Name'}
                    />
                    <TextFieldTW
                      data-test-id={'edit-signer-middle-name'}
                      name={'middleName'}
                      labelText={'Middle Name'}
                    />
                    <TextFieldTW
                      isRequired={true}
                      data-test-id={'edit-signer-last-name'}
                      name={'lastName'}
                      labelText={'Last Name'}
                    />
                  </div>
                  <section className={'mt-3'}>
                    <div className={'mb-1 flex'}>
                      <PhoneIcon className={'mr-1 h-5 w-5'} />
                      <Header5Text className={'mb-2'}>Contact info</Header5Text>
                    </div>

                    <div className={'flex gap-2'}>
                      <TextFieldTW data-test-id={'edit-signer-email'} name={'email'} labelText={'Email'} />
                      <PhoneNumberFieldTW
                        data-test-id={'edit-signer-phone-number'}
                        name={'phoneNumber'}
                        labelText={'Phone Number'}
                      />
                    </div>
                  </section>
                </section>
                <section className={'mt-4 flex justify-between'}>
                  <ColorlessButtonTW onClick={onDeleteClick} className={'bg-red-500 text-white'}>
                    <div className={'flex'}>
                      <>
                        {' '}
                        <TrashIcon className={'h-5 w-5 py-0.5'} /> Delete{' '}
                      </>
                    </div>
                  </ColorlessButtonTW>
                  <div className={'flex gap-2'}>
                    <ButtonTW type={'submit'} disabled={!isValid}>
                      <Show when={isSubmitting} fallback={'Save'}>
                        <SpinnerTW className={'border-white'} size={SpinnerSize.small} />
                      </Show>
                    </ButtonTW>
                  </div>
                </section>
              </Form>
            </>
          )}
        </Formik>
      </section>
    </div>
  );
};

const buildInitialFormData = (signer: ISignerResponse): EditSignerFormData => {
  return {
    id: signer.id,
    personId: signer.personId,
    firstName: signer.firstName,
    middleName: signer.middleName,
    lastName: signer.lastName,
    email: signer.email,
    phoneNumber: signer.phoneNumber,
  };
};

const EditSignerValidationSchema = yup.object().shape({
  firstName: yup.string().required(),
  middleName: yup.string().nullable(),
  lastName: yup.string().required(),
  email: yup.string().nullable().email(),
  phoneNumber: yup.string().nullable(),
});

const buildUpdateRequest = (
  formData: EditSignerFormData,
  appointment: ISigningAppointmentResponse
): ISigningAppointmentEditFormData => {
  // a list of the appointment signers, with the form's signer subbed in.
  const signers: IEditSignerRequest[] = (appointment.signers ?? []).map((signer) => {
    if (signer.id === formData.id) {
      return formData;
    }
    return signer;
  });

  return {
    id: appointment.id,
    signers,
  };
};
